import { createAsyncThunk } from "@reduxjs/toolkit";
import { toast } from "react-toastify";

import API, { showNetworkErrorToast } from "Utils/api";
import {
  addNewMissionPlan,
  removeMissionPlan,
  removeMissionVersion,
  setMissionVersions,
  updateMissionPlan,
  updateMissionDeploymentTime,
} from "Slices/missionPlans";
import { getOperatorPickedStation } from "Slices/operatorStation";
import { recordDispatchOrigin } from "Slices/dispatchOrigins";
import type { RootState } from "../../index";

export const fetchGetMissionPlans = createAsyncThunk(
  "missions/fetchGetMissionPlans",
  async (semanticMapId: string) => {
    try {
      const missions = await API.get(`missions/?semantic_map=${semanticMapId}`);

      return { missions: missions.data.results || [], semanticMapId };
    } catch (error) {
      showNetworkErrorToast(error);
      return Promise.reject();
    }
  }
);

export const fetchPostMissionPlan = createAsyncThunk<
  MissionAsset,
  MissionAsset
>("missions/fetchPostMissionPlan", async (data: MissionAsset, thunkAPI) => {
  try {
    const response = await API.post("missions/", data);
    thunkAPI.dispatch(addNewMissionPlan(response.data));
    toast.success("Mission plan Added");
    return response.data;
  } catch (error) {
    showNetworkErrorToast(error);
    return Promise.reject();
  }
});

export const fetchPatchMissionPlan = createAsyncThunk<
  void,
  { id: string; data: MissionAsset; hideToast: boolean }
>(
  "missions/fetchPatchMissionPlan",
  async (
    missionPlanData: { id: string; data: MissionAsset; hideToast: boolean },
    thunkAPI
  ) => {
    try {
      const response = await API.patch(
        `missions/${missionPlanData.id}/`,
        missionPlanData.data
      );
      await thunkAPI.dispatch(updateMissionPlan(response.data));
      if (!missionPlanData.hideToast) {
        toast.success("Mission plan Changed");
      }
    } catch (error) {
      showNetworkErrorToast(error);
      return Promise.reject();
    }
  }
);

export const fetchDeleteMissionPlan = createAsyncThunk<void, string>(
  "missions/fetchDeleteMissionPlan",
  async (id: string, thunkAPI) => {
    try {
      await API.delete(`missions/${id}`);
      await thunkAPI.dispatch(removeMissionPlan(id));
      toast.success("Mission plan Deleted");
    } catch (error) {
      showNetworkErrorToast(error);
      return Promise.reject();
    }
  }
);

export const fetchDeleteMissionVersion = createAsyncThunk<
  void,
  { missionId: string; versionNumber: number },
  { state: RootState }
>(
  "missions/fetchDeleteMissionVersion",
  async ({ missionId, versionNumber }, thunkAPI) => {
    // Helper: find the mission in state.data by masterMissionId and remove it
    const removeMissionFromUI = () => {
      const missionToRemove = thunkAPI
        .getState()
        .missionPlans.data.find(
          (m: MissionAsset) => (m as any).masterMissionId === missionId
        );
      if (missionToRemove) {
        thunkAPI.dispatch(removeMissionPlan(missionToRemove.id));
      }
    };

    try {
      await API.delete(`missions/${missionId}/versions/${versionNumber}`);

      // Update the local versions cache (the reducer only manages the cache,
      // it does NOT remove the mission from state.data).
      thunkAPI.dispatch(
        removeMissionVersion({ masterMissionId: missionId, versionNumber })
      );

      // Always verify whether the mission still has remaining versions via
      // the API. The local versions cache can be stale (e.g., a new version
      // was created after the cache was last populated), so we cannot rely
      // on it alone to decide whether to remove the mission from the UI.
      try {
        const response = await API.get(`/missions/${missionId}/versions/`);
        const remaining = response.data?.results ?? response.data;
        if (remaining && remaining.length > 0) {
          // Remaining versions exist — update the versions cache with fresh
          // data so the UI auto-switches to the next available version.
          thunkAPI.dispatch(
            setMissionVersions({
              masterMissionId: missionId,
              versions: remaining,
            })
          );
        } else {
          // No versions remain — remove the mission from the UI
          removeMissionFromUI();
        }
      } catch (err: any) {
        if (err?.response?.status === 404) {
          // Mission no longer exists on the backend — remove from UI
          removeMissionFromUI();
        }
        // For other errors, leave the mission in the list — user can refresh
      }

      // Show success toast after async verification is complete so the user
      // sees accurate feedback (e.g., mission removed vs. switched version).
      toast.success("Mission version deleted");
      return;
    } catch (error) {
      showNetworkErrorToast(error);
      return thunkAPI.rejectWithValue(error);
    }
  }
);

export const fetchDeployMissionPlan = createAsyncThunk(
  "missions/fetchDeployMissionPlan",
  async (
    data: {
      id: string;
      vehicle?: string;
      currentFleetId?: string;
      fleet?: string;
      stationId?: string;
      loops?: number;
      version?: string;
      masterMissionId?: string;
    },
    thunkAPI
  ) => {
    // Capture the operator's picked stop BEFORE the POST so that if the
    // operator switches picks between dispatch and WS confirmation, we still
    // record the stop that was active at the moment of dispatch.
    const pickedStation = getOperatorPickedStation(
      thunkAPI.getState() as RootState
    );
    const pickedStopIdAtDispatch: string | undefined = pickedStation?.id;

    try {
      const missionPlans = await API.post("/fleets/mission_queue/", {
        ...data,
        //it is requires to support tracking of an operator-launched mission
        mission: data.id,
        loops: data.loops || 1,
        version: data.version,
      });

      // Only refetch for regular missions, not uber missions (Call Vehicle)
      // Uber missions (identified by stationId presence) are fetched via /missions/operator_flow/
      // and excluded from /missions/{id}/ endpoint, causing 404 errors
      // See INFR-13279 for details
      if (!data.stationId) {
        // Refetch the deployed version to get updated deployment time
        const updatedMission = await API.get(`missions/${data.id}/`);

        // Update only deployment-related fields to preserve the latest version's updated_at
        thunkAPI.dispatch(
          updateMissionDeploymentTime({
            masterMissionId: data.masterMissionId,
            id: data.id,
            timeStamp: updatedMission.data.timeStamp,
            lastDeployedAnyVersion: updatedMission.data.lastDeployedAnyVersion,
          })
        );
      }

      // Record the dispatch origin so useInProgressVehicles can identify
      // missions dispatched FROM the picked station (FE-only, no BE migration
      // needed). Must be recorded after the POST so we have the mission ID.
      const createdMissionId: string | undefined = missionPlans.data?.id;
      if (createdMissionId && pickedStopIdAtDispatch) {
        thunkAPI.dispatch(
          recordDispatchOrigin({
            missionId: createdMissionId,
            stopId: pickedStopIdAtDispatch,
          })
        );
      }

      toast.success("Mission plan Deployed");
      return missionPlans.data;
    } catch (error) {
      showNetworkErrorToast(error);
      return Promise.reject();
    }
  }
);
